import { useState, useEffect } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  BsFillBarChartLineFill,
  BsPauseFill,
  BsPlayFill,
} from "react-icons/bs";
import { BsSkipStartFill } from "react-icons/bs";
import moment from "moment";
import {
  ChallengeModal,
  EditTimeModal,
  GroupTable,
  PauseModal,
  PlayAgainModal,
  ResetGameModal,
} from "./index";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../utils/crypto";
import { table } from "../utils/constant";

function Group({ sessionData, teamData, socket, guestParticipants=false }) {
  const [show, setShow] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [challengeModal, setChallengeModal] = useState(false);
  const [showPlayAgainModal, setShowPlayAgainModal] = useState(false);
  const [playAgainLoading, setPlayAgainLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const navigate = useNavigate();

  const sessionId = sessionData?.session?.id;
  const roundKey =
    teamData?.map((t) => `${t?.current_round ?? 0}-${t?.is_game_complete ?? 0}`).join(",") ?? "";
  
  useEffect(() => {
    if (!sessionId || !socket) return;
    socket.emit("fetch-team-performance-summary", { session_id: sessionId });
    const handler = (response) => {
      if (response?.status === true && Array.isArray(response.data)) {
        setSummaryData(response.data);
      } else {
        setSummaryData([]);
      }
      socket.off("team-performance-summary", handler);
    };
    socket.on("team-performance-summary", handler);
    return () => {
      socket.off("team-performance-summary", handler);
    };
  }, [sessionId, socket, roundKey]);

  let isGameComplete = sessionData?.session?.is_game_complete
    ? sessionData?.session?.is_game_complete === 1
    : false;

  let isGameStart =
    teamData?.length && teamData?.every((team) => team.current_status >= 3);
  const editModalShow = () => {
    setEditModal(!editModal);
  };
  const challengeModalShow = () => {
    setChallengeModal(!challengeModal);
  };

  const handleTeamPerform = () => {
    navigate(`/teams-performance/${encrypt(String(sessionData?.session?.id))}`);
  };

  let is_pause = sessionData?.session?.is_game_pause;

  const handlePause = async (type) => {
    const isGameOngoing =
      sessionData?.session?.game_start_time !== null &&
      sessionData?.session?.is_game_complete === 0;

    if (!socket) {
      console.error("Socket connection is not available.");
      return;
    }

    if (type === "pause_game" && isGameOngoing) {
      try {
        socket.emit(
          "game-timer-play-resume",
          {
            session: sessionData,
            teamData: teamData,
            type: type,
          },
          (response) => {
            if (response && response.status === 200) {
              setShow((prev) => !prev);
              is_pause = true;
            } else {
              console.error(
                "Failed to pause game:",
                response?.message || "Unknown error",
              );
            }
          },
        );
      } catch (error) {
        console.error("Error pausing game:", error);
      }
    } else if (isGameOngoing) {
      try {
        socket.emit(
          "game-timer-play-resume",
          {
            session: sessionData,
            teamData: teamData,
            type: type,
          },
          (response) => {
            if (response && response.status === 200) {
              is_pause = false;
            } else {
              console.error(
                "Failed to resume game:",
                response?.message || "Unknown error",
              );
            }
          },
        );
      } catch (error) {
        console.error("Error resuming game:", error);
      }
    }
  };

  let isDisabled = false;
  const getTeamStatus = teamData?.every((item) => item.current_status > 4);
  if (getTeamStatus) {
    if (sessionData?.session?.orientation_video_start && isGameComplete) {
      isDisabled = true;
    }
  } else {
    isDisabled = true;
  }
  const isPracticeRun = !!sessionData?.session?.is_practice_run;
  const isTeamActive = teamData?.every((item) => item.current_status === 5);
  const isGameActive =
    sessionData?.session?.game_start_time != null &&
    sessionData?.session?.is_game_complete === 0;

  const gameCompletedAt = sessionData?.session?.game_completed_at;
  const canResetPastSession =
    isGameComplete &&
    !isPracticeRun &&
    gameCompletedAt &&
    moment().diff(moment(gameCompletedAt), "hours") < 24;

  const handleResetConfirm = () => {
    if (!socket || !sessionData?.session?.id) return;
    setResetLoading(true);
    socket.emit(
      "reset-session",
      { session_id: sessionData.session.id, is_reset: true },
      (response) => {
        setResetLoading(false);
        if (response?.success) {
          setShowResetModal(false);
          navigate(`/teams/${encrypt(String(sessionData?.session?.id))}`);
          window.location.reload();
        }
      }
    );
  };

  const handlePlayAgainConfirm = () => {
    if (!socket || !sessionData?.session?.id) return;
    setPlayAgainLoading(true);
    socket.emit(
      "reset-session",
      { session_id: sessionData.session.id, is_reset: false },
      (response) => {
        setPlayAgainLoading(false);
        if (response?.success) {
          setShowPlayAgainModal(false);
          navigate(`/teams/${encrypt(String(sessionData?.session?.id))}`);
          window.location.reload();
        }
      }
    );  
  };

  return (
    <>
      <div className="hstack flex-wrap gap-3 mb-6">
        <Button
          disabled={isDisabled || isPracticeRun}
          variant="outline-primary"
          onMouseDown={(e) => {
            if (e.button === 0) {
              e.preventDefault();
              !is_pause ? setShow((prev) => !prev) : handlePause("resume_game");
            }
          }}
          onClick={(e) => e.preventDefault()}
        >
          {!is_pause ? (
            <span>
              <BsPauseFill className="me-1 mt-n1 text-lg" /> Pause Countdown
            </span>
          ) : (
            <>
              <BsPlayFill className="me-1 mt-n1 text-lg" /> Resume Countdown
            </>
          )}
        </Button>
        {isGameActive && isTeamActive && !isPracticeRun && (
          <Button
            variant="outline-primary"
            onClick={() => setShowResetModal(true)}
          >
            <span>
              <BsSkipStartFill className="me-1 mt-n1 text-lg" /> Restart Game
            </span>
          </Button>
        )}
        {!guestParticipants && isGameComplete && !isPracticeRun && (
          canResetPastSession ? (
            <Button
              variant="outline-primary"
              onClick={() => setShowPlayAgainModal(true)}
            >
              <span>
                <BsSkipStartFill className="me-1 mt-n1 text-lg" />{" "}
                {table?.playAgain}
              </span>
            </Button>
          ) : !canResetPastSession && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip className="tooltip-light">
                  {table?.resetGameTooltip24h}
                </Tooltip>
              }
            >
              <span className="d-inline-block">
                <Button variant="outline-primary" disabled>
                  <span>
                    <BsSkipStartFill className="me-1 mt-n1 text-lg" />{" "}
                    {table?.playAgain}
                  </span>
                </Button>
              </span>
            </OverlayTrigger>
          )
        )}
        {isGameComplete && !isPracticeRun && (
          <Button onClick={handleTeamPerform} variant="outline-primary">
            <BsFillBarChartLineFill className="me-2 mt-n1" />
            {table?.teamPerformance}
          </Button>
        )}
      </div>
      <GroupTable
        teamData={teamData}
        summaryData={summaryData}
        isGameComplete={isGameComplete}
        isGameStart={isGameStart}
        sessionData={sessionData}
        isPracticeRun={isPracticeRun}
      />
      {show && (
        <PauseModal
          show={show}
          hide={() => setShow((prev) => !prev)}
          handlePause={() => handlePause("pause_game")}
        />
      )}
      {editModal && (
        <EditTimeModal show={editModal} hide={() => editModalShow()} />
      )}
      {challengeModal && (
        <ChallengeModal show={true} hide={() => challengeModalShow()} />
      )}
      <PlayAgainModal
        show={showPlayAgainModal}
        hide={() => setShowPlayAgainModal(false)}
        loading={playAgainLoading}
        onContinue={() => handlePlayAgainConfirm()}
      />
      <ResetGameModal
        show={showResetModal}
        hide={() => setShowResetModal(false)}
        sessionName={sessionData?.session?.name || ""}
        loading={resetLoading}
        onConfirm={handleResetConfirm}
      />
    </>
  );
}
export default Group;
